import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { isEqual } from 'lodash';

import bulkUploadActions, { BULK_UPLOAD_UPDATE_SHEET_RESULT } from './actions';
import SheetPatients, { ReasonsSummary } from './SheetPatients';
import styles from './SheetPatients.module.scss';
import Table3, { columnHelper } from '../../../../containers/Table/Table3';
import Strings from '../../../../Strings';
import { PageHeader } from '../../../../components/PageHeader';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../../../components/PageHeader/Button';
import { notificationActions } from '../../../../components/Notification/redux/actions';
import Select from '../../../../components/Select';
import { Input } from '../../../../containers/Form';

const statusText = status => Strings.bulkUploadReview.sheetStatuses[status] || status;

function SheetDetails(props) {
  const { sheetId, getSheetDetail, getSheetRows, updateSheet, getSheetPatientSummary, showNotification, sheetSelect } = props;
  const [sheetDetails, setSheetDetails] = React.useState({});
  const [originalSheetDetails, setOriginalSheetDetails] = React.useState({});
  const [sheetRows, setSheetRows] = React.useState([]);
  const [sheetColumns, setSheetColumns] = React.useState([]);
  const [patientSummary, setPatientSummary] = React.useState({});
  const [selectedView, setSelectedView] = React.useState('Info');

  const refreshPatientSummary = React.useCallback(() => {
    getSheetPatientSummary(sheetId).then(r => {
      setPatientSummary(r?.response || {});
    });
  }, [sheetId, getSheetPatientSummary]);

  const refreshSheetDetails = () => {
    getSheetDetail(sheetId).then(r => {
      setSheetDetails(r?.response || {});
      setOriginalSheetDetails(r?.response || {});
    });
    getSheetRows(sheetId, { offset: 0 }).then(r => {
      setSheetRows(r?.response?.data || []);
      setSheetColumns(r?.response?.columns || []);
    });
    refreshPatientSummary();
  };

  React.useEffect(() => {
    refreshSheetDetails();
  }, [sheetId]);

  const columnsData = React.useMemo(() => {
    return sheetDetails?.column_mapping_config?.map(mapping => {
      const description = sheetDetails?.column_mapping_config[mapping?.enum_name];
      return {
        ...mapping,
        enum_description: description,
      };
    }) || [];
  }, [sheetDetails]);

  const onSaveSheet = () => {
    const payload = {
      column_mapping_config: sheetDetails.column_mapping_config,
      patient_creation_config: {auto_create_condition_by_icd: sheetDetails.patient_creation_config?.auto_create_condition_by_icd},
    };
    updateSheet(sheetId, payload).then(r => {
      if (r?.type !== BULK_UPLOAD_UPDATE_SHEET_RESULT) {
        showNotification(r?.response?.data?.message || Strings.error, 'error');
      } else {
        setOriginalSheetDetails(sheetDetails);
      }
    });
  };

  const onAcceptSheet = () => {
    const payload = { status: "in_progress" };
    updateSheet(sheetId, payload).then(r => {
      if (r?.type !== BULK_UPLOAD_UPDATE_SHEET_RESULT) {
        showNotification(r?.response?.data?.message || Strings.error, 'error');
      } else {
        refreshSheetDetails();
      }
    });
  };

  const fieldOptions = React.useMemo(() => Object.keys(sheetDetails?.enum_descriptions || {}).map(key => ({
    value: key,
    label: sheetDetails?.enum_descriptions[key],
  })), [sheetDetails]);

  const isEditable = React.useMemo(() => {
    return sheetDetails?.status === 'awaiting_confirmation';
  }, [sheetDetails]);

  // Retry re-plans the patients that failed (PUT in_progress resets them); it makes sense only
  // when there are failures and the sheet isn't already being processed.
  const canRetry = ['awaiting_review', 'finished'].includes(sheetDetails?.status)
    && (patientSummary?.counts_by_state?.error || 0) > 0;

  const fieldSelect = React.useCallback(row => {
    return (
      <Select
        value={row.original.enum_name}
        data={fieldOptions}
        onChange={e => setSheetDetails(prev => ({
          ...prev,
          column_mapping_config: prev.column_mapping_config.map(mapping => {
            if (mapping.excel_header === row.original.excel_header) {
              return {
                ...mapping,
                enum_name: e?.value,
                enum_description: e?.label,
              };
            }
            return mapping;
          }),
        }))}
        className={row.original.enum_name === 'unknown' ? 'invalid-control-background' : ''}
        isSearchable
        isClearable
        disabled={!isEditable}
      />
    );
  }, [fieldOptions, sheetDetails]);

  const indexInput = React.useCallback(row => {
    return (
      <Input
        type="number"
        value={row.original.index}
        onChange={e => setSheetDetails(prev => ({
          ...prev,
          column_mapping_config: prev.column_mapping_config.map(mapping => {
            if (mapping.excel_header === row.original.excel_header) {
              return {
                ...mapping,
                index: parseInt(e.target.value, 10),
              };
            }
            return mapping;
          }),
        }))}
        disabled={!isEditable}
      />
    );
  }, [sheetDetails]);

  const columns = React.useMemo(() => [
    columnHelper.accessor('excel_header', {
      header: 'Excel Column',
    }),
    columnHelper.accessor('enum_description', {
      header: 'Field',
      cell: info => fieldSelect(info.row),
    }),
    columnHelper.accessor('index', {
      header: 'Index',
      cell: info => indexInput(info.row),
    }),
  ], [fieldSelect]);

  // The sheet's columns in the file's order; a mapped column's header is highlighted and its
  // tooltip tells what it maps to.
  const rowsColumns = React.useMemo(() => {
    if (sheetRows.length === 0) return [];
    const columnHeader = column => {
      const mapped = !!column.enum_name;
      const tooltip = mapped
        ? Strings.formatString(Strings.bulkUploadReview.columnMappedTo, column.enum_name, column.index, column.description || '')
        : Strings.bulkUploadReview.columnNotUsed;
      return (
        <span className={mapped ? styles.mappedColumn : undefined} data-tooltip-id="tooltip" data-tooltip-content={tooltip}>
          {column.excel_header}
        </span>
      );
    };
    return [
      columnHelper.accessor('row_number', {
        header: Strings.bulkUploadReview.rowNumber,
        size: 80,
        enableHiding: false,
      }),
      columnHelper.accessor('reasons', {
        header: Strings.bulkUploadReview.issues,
        size: 250,
        enableHiding: false,
        enableSorting: false,
        cell: info => <ReasonsSummary reasons={info.getValue()} />,
      }),
      ...sheetColumns.map((column, i) => columnHelper.accessor(row => row.raw_data[i], {
        id: `cell_${i}`,
        header: () => columnHeader(column),
        meta: { label: column.excel_header },
        size: 150,
        cell: info => info.getValue() || '-',
      })),
    ];
  }, [sheetRows, sheetColumns]);

  const matchedColumnsCount = React.useMemo(() => {
    return sheetDetails?.column_mapping_config?.filter(c => c.enum_name !== 'unknown').length || 0;
  }, [sheetDetails]);

  return (
    <>
      <PageHeader
        left={`Sheet: ${sheetDetails?.sheet_name} (${statusText(sheetDetails?.status)})` || 'Sheet Details'}
        right={(
          <>
            {sheetSelect}
            {!isEqual(sheetDetails, originalSheetDetails) && (
              <Button title={Strings.saveConfiguration} onClick={onSaveSheet} class={HEADER_BUTTON_DARK_BLUE} />
            )}
            {isEditable && (
              <Button title={Strings.bulkUploadReview.prepareForReview} onClick={onAcceptSheet} class={HEADER_BUTTON_DARK_BLUE} />
            )}
            {canRetry && (
              <Button title={Strings.bulkUploadReview.retryFailed} onClick={onAcceptSheet} class={HEADER_BUTTON_DARK_BLUE} />
            )}
          </>
        )}
      />
      <div style={{ flex: 1, minHeight: 0 }}>
        <div className="config-tab-container">
          <div className="config-modules">
            <OverlayScrollbarsComponent
              defer
              options={{ scrollbars: { autoHide: 'leave', autoHideDelay: '100' } }}
              className="scrollbar-right-margin-min config-modules-nav"
            >
              <div className="config-modules-label">ToDo</div>
              <div className="tab-buttons-row config-modules-tabs">
                {["Info", "Columns", "Records", "Patients"].map(type => (
                  <button
                    key={type}
                    type="button"
                    className={`tabButton tab-label${selectedView === type ? ' active-tab-label' : ''}`}
                    onClick={() => setSelectedView(type)}
                  >
                    {type}
                    {type === 'Records' && sheetRows?.length > 0 && <span className="quantity-chip">{sheetRows.length}</span>}
                    {type === 'Columns' && matchedColumnsCount > 0 && <span className="quantity-chip">{matchedColumnsCount}</span>}
                    {type === 'Records' && sheetRows.some(row => row.reasons?.some(r => r.level === 'error')) && <span className="input-error module-error-icon left" />}
                    {type === 'Patients' && (patientSummary?.total || 0) > 0 && <span className="quantity-chip">{patientSummary.total}</span>}
                    {type === 'Patients' && (patientSummary?.counts_by_state?.error || 0) > 0 && <span className="input-error module-error-icon left" />}
                  </button>
                ))}
              </div>
            </OverlayScrollbarsComponent>
            <div
              className="config-modules-content"
            >
              {selectedView === 'Columns' ? (
                <Table3
                  name="sheet-columns-table"
                  columns={columns}
                  data={columnsData}
                  noPagination
                  pagination={{limit: columnsData.length}}
                />
              ) : null}
              {selectedView === 'Records' ? (
                <Table3
                  name="sheet-rows-table"
                  columns={rowsColumns}
                  data={sheetRows}
                />
              ) : null}
              {selectedView === 'Patients' ? (
                <SheetPatients
                  sheetId={sheetId}
                  patientSummary={patientSummary}
                  processing={sheetDetails?.status === 'in_progress'}
                  onDecided={refreshSheetDetails}
                />
              ) : null}
              {selectedView === 'Info' ? (
                <>
                  <div>{`Sheet Name: ${sheetDetails.sheet_name}`}</div>
                  <div>{`Status: ${statusText(sheetDetails.status)}`}</div>
                  <div>{`Matched Columns: ${matchedColumnsCount} / ${sheetDetails?.column_mapping_config?.length || 0}`}</div>
                  <div>{`Total Rows: ${sheetRows?.length || '-'}`}</div>
                  <Input
                    id="auto_create_condition_by_icd"
                    name="auto_create_condition_by_icd"
                    type="checkbox"
                    label="Automatically Create Conditions"
                    checked={sheetDetails?.patient_creation_config?.auto_create_condition_by_icd}
                    onChange={e => setSheetDetails(prev => ({
                      ...prev,
                      patient_creation_config: {
                        ...prev.patient_creation_config,
                        auto_create_condition_by_icd: e.target.checked,
                      },
                    }))}
                    disabled={!isEditable}
                    rowClassName="checkbox-row"
                  />
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

SheetDetails.propTypes = {
  sheetId: PropTypes.string.isRequired,
  getSheetDetail: PropTypes.func.isRequired,
  getSheetRows: PropTypes.func.isRequired,
  updateSheet: PropTypes.func.isRequired,
  getSheetPatientSummary: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
  sheetSelect: PropTypes.node,
};

const mapDispatchToProps = {
  getSheetDetail: bulkUploadActions.getSheetDetail,
  getSheetRows: bulkUploadActions.getSheetRows,
  updateSheet: bulkUploadActions.updateSheet,
  getSheetPatientSummary: bulkUploadActions.getSheetPatientSummary,
  showNotification: (message, timeout, error) => notificationActions.show(message, timeout, error),
};

export default connect(null, mapDispatchToProps)(SheetDetails);